/**
 * Hook for managing payment operations
 */

import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';

interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
}

interface PaymentVerification {
  status: string;
  type: string;
  credits_added?: number;
  tier?: string;
  message: string;
}

export const usePayments = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Purchase credits with a specific package
   */
  const purchaseCredits = async (packageId: string) => {
    setIsProcessing(true);

    try {
      const response = await apiClient.post<CheckoutResponse>(
        '/payments/checkout/credits',
        { package_id: packageId },
        { includeAuth: true }
      );

      // Redirect to Stripe Checkout
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);

      toast({
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Unable to initiate payment. Please try again.',
        variant: 'destructive',
      });

      setIsProcessing(false);
    }
  };

  /**
   * Upgrade to a subscription tier
   */
  const upgradeSubscription = async (tierId: string) => {
    setIsProcessing(true);

    try {
      const response = await apiClient.post<CheckoutResponse>(
        '/payments/checkout/subscription',
        { tier_id: tierId },
        { includeAuth: true }
      );

      // Redirect to Stripe Checkout
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Error creating subscription checkout:', error);

      toast({
        title: 'Subscription Error',
        description: error.response?.data?.detail || 'Unable to initiate subscription. Please try again.',
        variant: 'destructive',
      });

      setIsProcessing(false);
    }
  };

  /**
   * Open Stripe Customer Portal for managing subscriptions
   */
  const manageSubscription = async () => {
    setIsProcessing(true);

    try {
      const returnUrl = window.location.origin + '/preferences?tab=credits';

      const response = await apiClient.post<{ portal_url: string }>(
        '/payments/portal',
        { return_url: returnUrl },
        { includeAuth: true }
      );

      // Redirect to Stripe Customer Portal
      if (response.portal_url) {
        window.location.href = response.portal_url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error: any) {
      console.error('Error creating portal session:', error);

      toast({
        title: 'Portal Error',
        description: error.response?.data?.detail || 'Unable to open billing portal. Please try again.',
        variant: 'destructive',
      });

      setIsProcessing(false);
    }
  };

  /**
   * Verify a payment after redirect from Stripe
   */
  const verifyPayment = async (sessionId: string): Promise<PaymentVerification | null> => {
    try {
      const response = await apiClient.get<PaymentVerification>(
        `/payments/verify/${sessionId}`,
        { includeAuth: true }
      );

      return response;
    } catch (error: any) {
      console.error('Error verifying payment:', error);

      toast({
        title: 'Verification Error',
        description: 'Unable to verify payment status. Please contact support if credits were not added.',
        variant: 'destructive',
      });

      return null;
    }
  };

  return {
    purchaseCredits,
    upgradeSubscription,
    manageSubscription,
    verifyPayment,
    isProcessing,
  };
};
